import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getForum,
  getForumTransactionStatement,
  exportForumTransactionStatementXlsx,
} from '../../services/ForumService';
import { formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import CommonTable from '../components/CommonTable/CommonTable';
import { useCanDo } from '../../hooks/useCanDo';
import '../tasks/taskdetails.css';
import '../Documents/commonstyle.css';

const PAGE_SIZE = 50;

const ForumTransactionStatementList = () => {
  const { id: forumId } = useParams();
  const canExportForum = useCanDo('forum', 'export');
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [entityTitle, setEntityTitle] = useState('');
  const [forumInfo, setForumInfo] = useState({ name: '', id: null, start_date: null, due_date: null });

  const fetchUpdates = async (reset = false, loadMoreOffset = 0) => {
    const currentOffset = reset ? 0 : loadMoreOffset;
    try {
      setLoading(true);
      const response = await getForumTransactionStatement(forumId, {
        limit: PAGE_SIZE,
        offset: currentOffset,
      });
      const data = response.data?.data || response.data || [];
      const list = Array.isArray(data) ? data : (data.updates || data.list || []);
      if (reset) {
        setUpdates(list);
      } else {
        setUpdates((prev) => [...prev, ...list]);
      }
      setHasMore(list.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch transaction statement:', err);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchForumDetails = async () => {
    try {
      const response = await getForum(forumId);
      const forum = response.data?.forum || response.data?.data || response.data;
      const title = forum?.title || '';
      setEntityTitle(title || `Discussion #${forumId}`);
      setForumInfo({
        name: title,
        id: forum?.id || forumId,
        start_date: forum?.start_date || null,
        due_date: forum?.closed_at || null,
      });
    } catch {
      setEntityTitle(`Discussion #${forumId}`);
    }
  };

  useEffect(() => {
    if (forumId) {
      fetchForumDetails();
      fetchUpdates(true);
    }
  }, [forumId]);

  const loadMore = () => {
    if (!loading && hasMore && updates.length > 0) {
      fetchUpdates(false, updates.length);
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportForumTransactionStatementXlsx(forumId);
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (data?.success === false) {
          toast.error(data.message || 'No data to export');
          return;
        }
      }
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const name = (entityTitle || 'Discussion').replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `Forum_DailyUpdates_${name}_${forumId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Transaction statement exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      const errMsg = err.response?.data instanceof Blob
        ? 'Export failed'
        : (err.response?.data?.message || err.message || 'Failed to export transaction statement');
      toast.error(errMsg);
    }
  };

  const columns = [
    {
      key: 'index',
      header: '#',
      render: (row, rowIndex) => rowIndex + 1,
      headerStyle: { width: '50px' },
      cellStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'subject',
      header: 'Subject',
      accessor: (row) => row.Task?.title || row.task_title || '',
      headerStyle: { width: '12rem' },
      cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '12rem' },
    },
    {
      key: 'date',
      header: 'Date',
      render: (row) => formatDateTimeToDDMMMYYYY(row.updated_at),
      headerStyle: { width: '9.5rem' },
      cellStyle: { whiteSpace: 'nowrap' },
    },
    {
      key: 'updatedBy',
      header: 'Updated By',
      accessor: (row) => row.updatedByUser?.full_name || 'Unknown',
      headerStyle: { width: '10rem' },
      cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '10rem' },
    },
    {
      key: 'comment',
      header: 'Comment',
      render: (row) => {
        const val = row.update ?? row.dataValues?.update;
        const text = typeof val === 'string' ? val : (val != null ? String(val) : '');
        const commentId = row.id;
        if (forumId && commentId && text) {
          return (
            <Link
              to={`/forum/detail/${forumId}#comment-${commentId}`}
              className="text-primary"
              style={{ textDecoration: 'none', wordBreak: 'break-word' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {text}
            </Link>
          );
        }
        return text || '';
      },
      cellStyle: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '400px' },
    },
  ];

  return (
    <div className="task-daily-updates-list">
      <ToastContainer />
      <button
        type="button"
        className="back-button-link btn-link border-0 bg-transparent p-0"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: '#C42126',
          fontSize: '1rem',
          textDecoration: 'none',
          marginBottom: '1rem',
          cursor: 'pointer',
        }}
        onClick={() => navigate(-1)}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>

      <div className="card heightcard">
        <div className="card-header details-card-header text-start d-flex justify-content-between align-items-center">
          <h5 className="text-base sm:text-lg font-semibold mb-0">
            {entityTitle || `Discussion #${forumId}`}
          </h5>
          {canExportForum && updates.length > 0 && (
            <button
              className="btn btn-outline-primary btn-sm"
              type="button"
              onClick={handleExport}
              title="Download transaction statement as Excel"
            >
              <i className="fa fa-download me-1"></i>
              Download
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="daily-updates-project-info mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: '6px', fontSize: '0.95rem' }}>
            <div className="d-flex flex-wrap gap-3">
              <div>
                <span style={{ color: '#6c757d', fontWeight: '600' }}>Start:</span>{' '}
                <span>{forumInfo.start_date ? formatDateToDDMMMYYYY(forumInfo.start_date) : 'N/A'}</span>
              </div>
              <div>
                <span style={{ color: '#6c757d', fontWeight: '600' }}>End:</span>{' '}
                <span>{forumInfo.due_date ? formatDateToDDMMMYYYY(forumInfo.due_date) : 'N/A'}</span>
              </div>
            </div>
          </div>
          {loading && updates.length === 0 ? (
            <div className="text-center py-5 text-muted">Loading...</div>
          ) : (
            <>
              <CommonTable
                columns={columns}
                data={updates}
                emptyMessage="No comments or replies for this discussion yet."
                emptyStateIcon="fas fa-clipboard-list"
                getRowId={(row) => row.id}
                maxHeight="70vh"
                minHeight="300px"
              />
              {hasMore && updates.length > 0 && (
                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForumTransactionStatementList;
